// =============================================================================
// ESPOLIDAR WEB ADMIN - ANALYSIS PAGE
// Pagina per analisi avanzata degli eventi
// =============================================================================

import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3,
  Download,
  Filter,
  Calendar,
  Cpu,
  Target,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
} from 'lucide-react'
import { analysisApi, devicesApi } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const eventTypeLabels: Record<string, string> = {
  radar_enter: 'Radar Ingresso',
  radar_exit: 'Radar Uscita',
  lidar_enter: 'LIDAR Ingresso',
  lidar_exit: 'LIDAR Uscita',
}

const eventTypeColors: Record<string, string> = {
  radar_enter: 'bg-blue-100 text-blue-700',
  radar_exit: 'bg-blue-50 text-blue-500',
  lidar_enter: 'bg-green-100 text-green-700',
  lidar_exit: 'bg-green-50 text-green-500',
}

export default function AnalysisPage() {
  // Filtri
  const [deviceFilter, setDeviceFilter] = useState<string>('')
  const [zoneFilter, setZoneFilter] = useState<number | ''>('')
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('')
  const [fromDate, setFromDate] = useState<string>(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0]
  })

  // Paginazione
  const [page, setPage] = useState(0)
  const limit = 100

  // UI State
  const [showFilters, setShowFilters] = useState(true)
  const [isExporting, setIsExporting] = useState(false)

  // Fetch dispositivi
  const { data: devices } = useQuery({
    queryKey: ['devices'],
    queryFn: devicesApi.list,
  })

  // Fetch zone (filtrate per dispositivo se selezionato)
  const { data: zones } = useQuery({
    queryKey: ['analysisZones', deviceFilter],
    queryFn: () => analysisApi.getZones(deviceFilter || undefined),
  })

  // Fetch eventi con filtri
  const {
    data: eventsData,
    isLoading: isLoadingEvents,
    isFetching: isFetchingEvents,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ['analysisEvents', deviceFilter, zoneFilter, eventTypeFilter, fromDate, toDate, page],
    queryFn: () =>
      analysisApi.getEvents({
        device_id: deviceFilter || undefined,
        zone_id: zoneFilter || undefined,
        event_type: eventTypeFilter || undefined,
        from_date: fromDate ? `${fromDate}T00:00:00` : undefined,
        to_date: toDate ? `${toDate}T23:59:59` : undefined,
        skip: page * limit,
        limit,
      }),
  })

  // Fetch summary
  const { data: summary } = useQuery({
    queryKey: ['analysisSummary', deviceFilter, fromDate, toDate],
    queryFn: () =>
      analysisApi.getSummary({
        device_id: deviceFilter || undefined,
        from_date: fromDate ? `${fromDate}T00:00:00` : undefined,
        to_date: toDate ? `${toDate}T23:59:59` : undefined,
      }),
  })

  // Reset zone filter quando cambia dispositivo
  useEffect(() => {
    setZoneFilter('')
  }, [deviceFilter])

  // Reset pagina quando cambiano i filtri
  useEffect(() => {
    setPage(0)
  }, [deviceFilter, zoneFilter, eventTypeFilter, fromDate, toDate])

  // Export CSV
  const handleExport = useCallback(async () => {
    setIsExporting(true)
    try {
      const blob = await analysisApi.exportEvents({
        device_id: deviceFilter || undefined,
        zone_id: zoneFilter || undefined,
        event_type: eventTypeFilter || undefined,
        from_date: fromDate ? `${fromDate}T00:00:00` : undefined,
        to_date: toDate ? `${toDate}T23:59:59` : undefined,
      })

      // Download file
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `eventi_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success('Export completato')
    } catch (error) {
      toast.error('Errore durante l\'export')
      console.error('Export error:', error)
    } finally {
      setIsExporting(false)
    }
  }, [deviceFilter, zoneFilter, eventTypeFilter, fromDate, toDate])

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const totalPages = eventsData ? Math.ceil(eventsData.total / limit) : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="text-primary-600" />
          Analisi Eventi
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetchEvents()}
            className="btn btn-secondary flex items-center gap-2"
            disabled={isFetchingEvents}
          >
            <RefreshCw size={16} className={isFetchingEvents ? 'animate-spin' : ''} />
            Aggiorna
          </button>
          <button
            onClick={handleExport}
            className="btn btn-primary flex items-center gap-2"
            disabled={isExporting || !eventsData?.total}
          >
            {isExporting ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Download size={16} />
            )}
            Esporta CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">Totale Eventi</div>
            <div className="text-2xl font-bold text-gray-900">{summary.total_events.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">Radar Ingresso</div>
            <div className="text-2xl font-bold text-blue-600">
              {(summary.by_type.radar_enter || 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">LIDAR Ingresso</div>
            <div className="text-2xl font-bold text-green-600">
              {(summary.by_type.lidar_enter || 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">Dispositivi Attivi</div>
            <div className="text-2xl font-bold text-purple-600">{summary.by_device.length}</div>
          </div>
        </div>
      )}

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 rounded-t-xl"
        >
          <div className="flex items-center gap-2 font-medium text-gray-700">
            <Filter size={18} />
            Filtri di Ricerca
          </div>
          {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showFilters && (
          <div className="p-4 pt-0 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar size={14} className="inline mr-1" />
                  Data Inizio
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar size={14} className="inline mr-1" />
                  Data Fine
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="input w-full"
                />
              </div>

              {/* Dispositivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Cpu size={14} className="inline mr-1" />
                  Dispositivo
                </label>
                <select
                  value={deviceFilter}
                  onChange={(e) => setDeviceFilter(e.target.value)}
                  className="input w-full"
                >
                  <option value="">Tutti i dispositivi</option>
                  {devices?.map((device) => (
                    <option key={device.device_id} value={device.device_id}>
                      {device.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Zona */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Target size={14} className="inline mr-1" />
                  Zona
                </label>
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value ? Number(e.target.value) : '')}
                  className="input w-full"
                  disabled={!zones?.length}
                >
                  <option value="">Tutte le zone</option>
                  {zones?.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name} ({zone.device_name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo Evento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FileSpreadsheet size={14} className="inline mr-1" />
                  Tipo Evento
                </label>
                <select
                  value={eventTypeFilter}
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                  className="input w-full"
                >
                  <option value="">Tutti i tipi</option>
                  <option value="radar_enter">Radar Ingresso</option>
                  <option value="radar_exit">Radar Uscita</option>
                  <option value="lidar_enter">LIDAR Ingresso</option>
                  <option value="lidar_exit">LIDAR Uscita</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabella Eventi */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {eventsData ? (
              <>
                Mostrando {eventsData.events.length} di {eventsData.total.toLocaleString()} eventi
              </>
            ) : (
              'Caricamento...'
            )}
          </div>

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn btn-secondary btn-sm"
              >
                Precedente
              </button>
              <span className="text-sm text-gray-600">
                Pagina {page + 1} di {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn btn-secondary btn-sm"
              >
                Successiva
              </button>
            </div>
          )}
        </div>

        {isLoadingEvents ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : eventsData?.events.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Data/Ora
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Tipo Evento
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Dispositivo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Zona
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Dettagli
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {eventsData.events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatTimestamp(event.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          eventTypeColors[event.event_type] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {eventTypeLabels[event.event_type] || event.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{event.device_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {event.zone ? (
                        <span className="px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs">
                          {event.zone.name}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {event.event_data && Object.keys(event.event_data).length > 0 ? (
                        <details className="cursor-pointer">
                          <summary className="text-primary-600 hover:text-primary-700">
                            Mostra
                          </summary>
                          <pre className="mt-1 text-xs bg-gray-50 p-2 rounded max-w-xs overflow-auto">
                            {JSON.stringify(event.event_data, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Nessun evento trovato con i filtri selezionati
          </div>
        )}

        {/* Paginazione Bottom */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn btn-secondary btn-sm"
            >
              Precedente
            </button>
            <span className="text-sm text-gray-600">
              Pagina {page + 1} di {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn btn-secondary btn-sm"
            >
              Successiva
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
